import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  BarChart3, 
  Leaf, 
  Recycle, 
  Zap, 
  Settings,
  Factory,
  MessageSquare,
  Bell,
  FileText,
  Menu,
  X,
  Box,
  Upload
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Data Upload', href: '/data-upload', icon: Upload },
    { name: 'MRV Flows', href: '/mrv', icon: BarChart3 },
    { name: 'Climate Metrics', href: '/climate-metrics', icon: Leaf },
    { name: 'Circular Waste', href: '/circular-waste', icon: Recycle },
    { name: 'Biogas Integration', href: '/biogas', icon: Zap },
    { name: 'Scenario Modeling', href: '/scenarios', icon: Settings },
    { name: 'Digital Twin', href: '/digital-twin', icon: Box },
    { name: 'AI Assistant', href: '/ai-assistant', icon: MessageSquare },
    { name: 'Smart Alerts', href: '/smart-alerts', icon: Bell },
    { name: 'MRV Generator', href: '/mrv-generator', icon: FileText },
  ];

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile menu button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg"
      >
        {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-64 bg-white shadow-lg transform transition-transform duration-300
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col px-6 py-4 border-b">
          <div className="flex items-center mb-2">
            <img 
              src="/images/NxES.JPG" 
              alt="NxES" 
              className="h-12 w-12 rounded-lg object-contain mr-3"
            />
            <div>
              <h1 className="text-base font-bold text-gray-900">NxES Climate Control</h1>
              <p className="text-xs text-gray-500">AI Platform</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
            <img 
              src="/images/logo.jpg" 
              alt="Cloudcognoscente" 
              className="h-6 w-6 rounded object-cover"
            />
            <span className="text-xs text-gray-600">by Cloudcognoscente</span>
          </div>
        </div>
        
        <nav className="mt-6 overflow-y-auto h-[calc(100vh-100px)]">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Icon className="h-5 w-5 mr-3" />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Overlay for mobile */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      
      {/* Main content */}
      <div className="flex-1 overflow-auto">
        <main className="p-4 lg:p-6 pt-16 lg:pt-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
